"use client"
import {useState, useEffect} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import { ArrowLeft, Download, MapPin, XCircle, CreditCard } from "lucide-react"
import Sidebar from './Sidebar';
import { useTheme } from "../../../../../redux/hooks/useTheme";
import BillingService from '../../../../services/BillingService';
import { toast } from 'react-toastify';
import {X} from 'lucide-react';
import { useUserDetails } from "../../../../../redux/hooks/UserDetails";
import { Loader2 } from 'lucide-react';
import {useScript} from "../../../../../redux/hooks/useScript";
import Loader from "../../../../Loader/Loader";
import Header from '../Header';
import Footer from '../Footer';
import './Billing.css'

declare global {
    interface Window {
      Chargebee: {
        init(config: { site: string; publishableKey: string }): void;
        getInstance(): any;
      };
    }
  }
interface Invoice {
    invoice_id: string;
    billing_date: string;
    plan: string;
    amount: number;
    status: string;
    currency: string;
    due_date: string;
    paid_at?: string;
  }

interface PaymentMethod {
    brand: string;
    last4: string;
    expMonth: string;
    expYear: string;
}
export default function Billing() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true); 
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [cardElement, setCardElement] = useState<any>(null);
  const [error, setError] = useState('');
//   const [currentPaymentMethod, setCurrentPaymentMethod] = useState(null);
//   const [currentPaymentMethod, setCurrentPaymentMethod] = useState<PaymentMethod | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const { theme } = useTheme();
  const navigate = useNavigate();
  const billingService = new BillingService();
  const { userDetail } = useUserDetails();
  const scriptStatus = useScript('https://js.chargebee.com/v2/chargebee.js');

  // Update your useEffect to use scriptStatus
  useEffect(() => {
    if (scriptStatus === 'ready' && showPaymentPopup) {
      initializeChargebee();
    }
    
    // Cleanup function
    return () => {
      if (cardElement) {
        cardElement.unmount();
      }
    };
  }, [scriptStatus, showPaymentPopup]);
  const fetchInvoices = async () => {
    setLoading(true);
    const response = await billingService.getInvoices();
    if (response.success && response.data) {
        console.log("Invoices fetched successfully:", response.data);
      setInvoices(response.data);
    } else {
      console.log(response.error);
    }
    setLoading(false);
  };

  const handleDownloadInvoice = async (invoiceId: string) => {
    try {
      const response = await billingService.downloadInvoice(invoiceId);
      if (response.success && response.data) {
        const link = document.createElement('a');
        link.href = response.data.download_url;
        link.setAttribute('download', `invoice-${invoiceId}.pdf`); 
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.log(response.error);
      }
    } catch (error) {
      console.log('Download failed:', error);
    }
  };
  const handleCancelPlan = async () => {
    // if (!cancelReason.trim()) {
    //   toast.error('Please provide a reason for cancellation');
    //   return;
    // }
    let cancelReason = "Cancellation request"
  
    try {
      const response = await billingService.cancelSubscription(cancelReason);
      if (response.success) {
        toast.success('Subscription cancellation requested. Finalizing...');
        setShowCancelPopup(false);
        pollForCancellation();
      } else {
        toast.error(response.error || 'Failed to cancel subscription');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    }
  };
  const pollForCancellation = async () => {
    setIsPolling(true);
    let attempts = 0;
    const maxAttempts = 30;
    const poll = async () => {
      try {
        const res = await billingService.getSubscriptionStatus();
        if (res?.status === 'cancelled' || res?.status === 'canceled') {
          setIsPolling(false);
          toast.success('Subscription cancelled and confirmed!');
          setTimeout(() => window.location.reload(), 1500);
          return;
        }
        attempts += 1;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setIsPolling(false);
          toast.error('Cancellation is taking longer than expected. Please refresh later.');
        }
      } catch (err) {
        attempts += 1;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setIsPolling(false);
          toast.error('Cancellation is taking longer than expected. Please refresh later.');
        }
      }
    };
    poll();
  };
  useEffect(() => {
    if (showPaymentPopup) {
      initializeChargebee();
    }
  }, [showPaymentPopup]);

//   const handleScriptLoad = () => {
//     setScriptLoaded(true);
//     if (showPaymentPopup) {
//       initializeChargebee();
//     }
//   };


const initializeChargebee = async () => {
    try {
      if (!window.Chargebee) {
        throw new Error('Chargebee not loaded');
      }
  
      // Initialize Chargebee first
      window.Chargebee.init({
        site: process.env.REACT_APP_CHARGEBEE_SITE_URL || 'Autoolic-test', // Use your site URL from .env
        publishableKey: process.env.REACT_APP_CHARGEBEE_API_KEY || 'test_ICcdDr3T4lqIICQlGbkVcuGe968cdKl451i' // Use your API key from .env
      });
  
      // Get the instance after initialization
      const chargebee = window.Chargebee.getInstance();
  
      // Create card component
      const card = chargebee.create('card', {
        style: {
          base: {
            color: theme.textColor,
            fontSize: '16px',
            '::placeholder': {
              color: '#999999'
            }
          },
          invalid: {
            color: '#ef4444'
          }
        }
      });
  
      // Mount the card element
      await card.mount('#card-element');
      setCardElement(card);
  
      // Add event listener for changes
      card.on('change', (event: any) => {
        if (event.error) {
          setError(event.error.message);
        } else {
          setError('');
        }
      });
  
      setScriptLoaded(true);
  
    } catch (error: any) {
      console.error('Error initializing Chargebee:', error);
      setError(error.message || 'Failed to initialize payment form');
      setScriptLoaded(false);
    }
  };

  const formatRenewalDate = (dateString: string): string => {
    const date = new Date(dateString);
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    // Add ordinal suffix to day (1st, 2nd, 3rd, etc.)
    const getOrdinalSuffix = (d: number): string => {
      if (d > 3 && d < 21) return 'th';
      switch (d % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
  
    return `${month} ${day}${getOrdinalSuffix(day)}, ${year}`;
  };
  const getUniqueInvoices = (invoices: Invoice[]) => {
    const uniqueInvoices = new Map();
    invoices.forEach(invoice => {
      if (!uniqueInvoices.has(invoice.invoice_id)) {
        uniqueInvoices.set(invoice.invoice_id, invoice);
      }
    });
    return Array.from(uniqueInvoices.values());
  };
  
  // Update the useEffect hook
  useEffect(() => {
    if (scriptStatus === 'ready' && showPaymentPopup) {
      initializeChargebee();
    }
  
    return () => {
      if (cardElement) {
        try {
          cardElement.unmount();
        } catch (error) {
          console.error('Error unmounting card element:', error);
        }
      }
    };
  }, [scriptStatus, showPaymentPopup]);
  
  // Update the handleUpdatePaymentMethod function
  const handleUpdatePaymentMethod = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
  
    try {
      if (!cardElement) {
        throw new Error('Card element not initialized');
      }
  
      const result = await cardElement.tokenize();
      if (!result.token) {
        throw new Error('Could not generate payment token');
      }
  
      const response = await billingService.updatePaymentMethod(result.token);
      
      if (response.success) {
        toast.success('Payment method updated successfully');
        setShowPaymentPopup(false);
        // Optionally refresh payment method display
        // fetchCurrentPaymentMethod();
      } else {
        setError(response.error || 'Failed to update payment method');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to process payment update');
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  useEffect(() => {
    if (userDetail?.ogUserId) {
      navigate("*");
    }
  }, [userDetail, navigate]);

  const handleTalkToSupport = () => {
    // If not logged in or missing user details, open chatbot and send message without user data
    if (!userDetail || !userDetail.email || !userDetail.name) {
      if (window.ChatbotBuilder) {
        if (typeof window.ChatbotBuilder.open === "function") {
          window.ChatbotBuilder.open();
        }
        window.ChatbotBuilder.sendMessage(
          `Hello. I need help with my subscription cancellation.`
        );
      }
      return;
    }

    // If user is org user, show error and do not open chatbot
    if (userDetail.ogUser) {
      toast.error(
        <div>
          <p className="font-medium">Organization User</p>
          <p className="text-sm">Please contact your organization administrator for support.</p>
        </div>,
        {
          autoClose: 5000,
          position: "top-center",
        }
      );
      return;
    }

    // If user is logged in and not an org user, open chatbot, set user data, and send message
    if (window.ChatbotBuilder) {
      window.ChatbotBuilder.setUserData({
        name: userDetail.name || '',
        email: userDetail.email || ''
      });
      console.log("User data sent to chatbot:", userDetail.name, userDetail.email);
      if (typeof window.ChatbotBuilder.open === "function") {
        window.ChatbotBuilder.open();
      }
      window.ChatbotBuilder.sendMessage(
        `Hello. I need help with my subscription cancellation.`
      );
    }
    
    // Close the popup after opening chatbot
    setShowCancelPopup(false);
  };

  return (
    <>
     <Header noPadding={true} addBottomBorder={true} isDifferentStyle={true} paddingTop={true} hideBorder paddingX={true} paddingY={true}  />
     {isPolling && (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black bg-opacity-40">
      <Loader isLoading={true} text="Finalizing cancellation, please wait..." />
    </div>
    )}
    
    <div className='userprofile-bg'>
        <div style={{}} className="mx-auto billing billing-mob">
            <div className="flex flex-col p-[10px] pb-[40px] md:flex-row gap-[16px] tab-flex">
                {/* Sidebar */}
                <Sidebar/>

                {/* Main Content */}
                <div className="flex-1 w-full pt-[40px] billing-main">
                    {/* Current Plan */}
                    <div className="mb-8">
                        <h2 className="billing-h mb-4">Current Plan</h2>
                        {userDetail && userDetail.ogSubscriptionId && userDetail.ogSubscriptionId.planName!=="noPlan_m" ? 
                        <div style={{display:'flex', justifyContent:'space-between'}} className="billing-cards p-6 rounded-[20px] shadow-sm border">
                            <div className="flex w-full  md:items-center justify-between space-between current-plan-biling">               
                                <div className="flex  items-center mb-4 md:mb-0">
                                    <div className="px-[16px] button-premium py-[8px] rounded-[8px] text-sm font-medium flex items-center" style={{ color: theme.buttonBackground }}>
                                          <span className='standard'>
                                             <span className='no-active-plansno-active-plans'>{ userDetail.ogSubscriptionId.display_Plan_name}</span>
                                          </span>
                                          {(userDetail.ogSubscriptionId.planName!=="noPlan_m" && userDetail.ogSubscriptionId.planName!=="free_y") &&   
                                          <span className='monthly'>    
                                                <span>
                                                  <span className="mx-1 monthly">-</span>  
                                                    {userDetail.ogSubscriptionId.monthlyPlan ? 'Monthly' : 'Yearly'}
                                               </span>                                             
                                         </span>
                                         }
                                    </div>
                                    {(userDetail.ogSubscriptionId.planName!=="noPlan_m" && userDetail.ogSubscriptionId.planName!=="free_y") &&
                                    <div className="ml-4">
                                        <span className="permonth">{userDetail.ogSubscriptionId.price }$</span>
                                        <span className="permonth-sub"> /<span className='permonth-sub'> {userDetail.ogSubscriptionId.monthlyPlan ? 'Per Month' : 'Per Year' }</span>
                                       </span>
                                    </div>}
                                </div>
                                {(userDetail.ogSubscriptionId.planName==="free_y" && userDetail?.subscriptionId?.trial?.isTrial) &&
                                     <div className="permonth-sub">Your free trial ends on &nbsp;
                                       {formatRenewalDate(userDetail.subscriptionId?.trial?.endDate)}                   
                                     </div>
                                    }
                                <div style={{width:userDetail.ogSubscriptionId.planName==="free_y" ? 'auto' : '55%',alignItems:'center', justifyContent:'space-between'}} className="flex items-center justify-between md:justify-end w-full md:w-auto bmob-w">         
                                    {(userDetail.ogSubscriptionId.planName!=="noPlan_m" && userDetail.ogSubscriptionId.planName!=="free_y") &&
                                     <div className="permonth-sub">Next renewal on &nbsp;
                                       {formatRenewalDate(userDetail?.subscriptionId?.billing?.subscription?.end)}                   
                                     </div>
                                    }
                                    <button className="px-[16px] py-[8px] go-live rounded-[8px] text-sm hover:opacity-90 chn-plan-mob"
                                        onClick={()=>navigate("/planinfo")}>
                                       <span style={{fontWeight:500}} className='gradient-text text-[13px]'>Choose Plan</span>
                                    </button>
                                </div>
                            </div>
                        </div> :
                        <div style={{display:'flex', justifyContent:'space-between', width:'100%'}} className="billing-cards p-6 rounded-[20px] shadow-sm border">
                            <div className="flex w-full flex-col md:items-center justify-between">
                                {userDetail && userDetail.ogSubscriptionId && 
                                      <div className="flex flex-col items-center mb-4 md:mb-0">
                                            {userDetail.ogSubscriptionId.planName==="noPlan_m" &&  
                                              <span>
                                                <img  src={theme.noPlans} alt="Noe plan" className="w-full h-auto object-cover"/>
                                              </span>
                                            }
                                            <span className='standard'>
                                             <span className='no-active-plansno-active-plans'>No Active Plan!</span>
                                           </span>
                                           <button className="mt-[24px] px-[16px] py-[8px] go-live rounded-[8px] text-sm hover:opacity-90 chn-plan-mob"
                                             onClick={()=>navigate("/planinfo")}>
                                             <span style={{fontWeight:500}} className='gradient-text text-[13px]'>{userDetail?.ogSubscriptionId?.planName === "noPlan_m"
      ? "Select Plan": "Choose plan"}</span>
                                          </button>
                                      </div>  
                                }
                            </div>  
                        </div>  
                        }                
                    </div>

                    {/* Billing Address & Payment Method */}
                   {userDetail && userDetail.ogSubscriptionId && (userDetail.ogSubscriptionId.planName!=="noPlan_m" && userDetail.ogSubscriptionId.planName!=="free_y") && 
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        {/* Billing Address */}
                        {userDetail && userDetail.subscriptionId && userDetail.subscriptionId.billingAddress &&
                        <div>
                            <h2 className="billing-h mb-4">Billing Address</h2>
                            <div style={{height:'100px'}} className="billing-cards flex justify-between p-6 rounded-[20px] shadow-sm border border-gray-100 flex justify-between p-6 rounded-[20px] shadow-sm border border-gray-100">
                                <div className="flex justify-between w-full">
                                    <div className="flex">
                                    <MapPin className="h-5 w-5 permonth-sub mr-2 mt-0.5" />
                                    <div className='permonth-sub'>
                                        <p className="permonth-sub">{userDetail.subscriptionId.billingAddress.addressLine1 ? userDetail.subscriptionId.billingAddress.addressLine1 : userDetail.subscriptionId.billingAddress.addressLine2 }</p>
                                        <p className="permonth-sub">{userDetail.subscriptionId.billingAddress.city} - {userDetail.subscriptionId.billingAddress.postalCode}, {userDetail.subscriptionId.billingAddress.state}</p>
                                        <p className="permonth-sub">{userDetail.subscriptionId.billingAddress.country}</p>
                                    </div>
                                    </div>
                                    {/* <button className="px-[16px] py-[8px] go-live  rounded-[8px] hover:opacity-90"
                                        style={{ }}>
                                       <span className='gradient-text'>Change</span>
                                    </button> */}
                                </div>
                                   
                            </div>
                        </div>
                        }

                        {/* Payment Method */}
                        <div>
                            {/* Payment Method Card */}
                            {/* <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100"> */}
                                <h2 className="billing-h mb-4">Payment Method</h2>
                                <div  style={{height:'100px'}} className="billing-cards p-6 rounded-[20px] shadow-sm border border-gray-100">
                                    <div className="flex justify-between items-start">
                                    <div className="flex">
                                        <CreditCard className="h-6 w-6 text-gray-400 mr-3" />
                                        <div>
                                        {userDetail && userDetail.subscriptionId && userDetail.subscriptionId.creditCards && userDetail.subscriptionId.creditCards.length > 0 ? (
                                            <>
                                            <p className="credit-card">
                                                {/* {currentPaymentMethod.brand} •••• {currentPaymentMethod.last4} */}
                                           <span className='card-name'> {userDetail.subscriptionId.creditCards[0].cardType} •••• {userDetail.subscriptionId.creditCards[0].lastFourDigits}</span>
                                            </p>
                                            <p className="card-exp">
                                                Expires {userDetail.subscriptionId.creditCards[0].expiryMonth}/{userDetail.subscriptionId.creditCards[0].expiryYear}
                                            </p>
                                            </>
                                        ) : (
                                            <p className="text-gray-700">No payment method </p>
                                        )}
                                        </div>
                                    </div>
                                    {/* <button
                                        className="px-3 py-1 border rounded-md text-sm hover:opacity-90"
                                        style={{ borderColor: theme.buttonBackground, color: theme.buttonBackground }}
                                        onClick={() => setShowPaymentPopup(true)}
                                    >
                                        Add
                                    </button> */}
                                    </div>
                               </div>

                            {/* Payment Update Modal */}
                            {showPaymentPopup && (
                                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                                <div 
                                    className="relative w-full max-w-md p-6 rounded-lg shadow-lg"
                                    style={{ 
                                    backgroundColor: theme.background,
                                    border: `1px solid ${theme.borderColor}`
                                    }}
                                >
                                    <div className="flex justify-between items-center mb-[12px]">
                                    <h3 className="text-lg font-semibold" style={{ color: theme.headingColor }}>
                                        {/* {currentPaymentMethod ? 'Update Payment Method' : 'Add Payment Method'} */}Add Payment
                                    </h3>
                                    <button 
                                        onClick={() => setShowPaymentPopup(false)}
                                        className="p-1 rounded-full hover:bg-gray-100"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                    </div>
                                        <form onSubmit={handleUpdatePaymentMethod}>
                                            <div className="mb-4">
                                                <div 
                                                id="card-element"
                                                className="p-3 border rounded-md"
                                                style={{
                                                    backgroundColor: theme.inputBackground,
                                                    borderColor: error ? '#ef4444' : theme.borderColor
                                                }}
                                                />
                                                {error && (
                                                <p className="mt-2 text-sm text-red-600">{error}</p>
                                                )}
                                            </div>

                                            <div className="flex justify-end gap-3">
                                                <button
                                                type="button"
                                                onClick={() => setShowPaymentPopup(false)}
                                                className="px-4 py-2 border rounded-md text-sm"
                                                style={{ 
                                                    borderColor: theme.borderColor,
                                                    color: theme.textColor
                                                }}
                                                disabled={isLoading}
                                                >
                                                Cancel
                                                </button>
                                                <button
                                                type="submit"
                                                disabled={isLoading || !scriptLoaded}
                                                className="px-4 py-2 rounded-md text-sm text-white flex items-center"
                                                style={{
                                                    backgroundColor: theme.buttonBackground,
                                                    opacity: (isLoading || !scriptLoaded) ? 0.7 : 1
                                                }}
                                                >
                                                {isLoading ? (
                                                    <>
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    Processing...
                                                    </>
                                                ) : (
                                                    'Save Payment Method'
                                                )}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}
                      </div>
                    
                    </div>}

                    {/* Invoices */}
                    <div className="mb-8">
                        <h2 className="billing-h mb-4">Invoices</h2>
                        <div className="billing-cards rounded-[20px] px-[20px] overflow-hidden">
                            <div className="overflow-x-auto">
                               {invoices && invoices.length>0 ?
                                <table className="min-w-full">
                                    <thead className="">
                                    <tr className="text-left billing-tb-header">
                                        <th className="px-6 py-[20px]">Invoice ID</th>
                                        <th className="px-6 py-[19px]">Billing Date</th>
                                        <th className="px-6 py-[19px]">Plan</th>
                                        <th className="px-6 py-[19px]">Amount</th>
                                        <th className="px-6 py-[19px]">Status</th>
                                        <th className="px-6 py-[19px]"></th>
                                    </tr>
                                    </thead>
                                    <tbody className=''>
                                        { getUniqueInvoices(invoices).map((invoice) => (
                                            <tr key={invoice.invoice_id} className="billing-cards-b border-b">
                                            <td className="px-6 py-[19px] billing-tb-data">#{invoice.invoice_id}</td>
                                            <td className="px-6 py-[19px] billing-tb-data">
                                                {new Date(invoice.billing_date).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-[19px] billing-tb-data">{invoice.plan}</td>
                                          
                                            <td className="px-6 py-[19px] billing-tb-data">$ {invoice.amount}</td>
                                            <td className="px-6 py-[19px] billing-tb-data">
                                                <span className={`px-[25px] py-[5px] rounded-full text-xs ${
                                                'bg-[#e1ffdc] text-[#07a104]'
                                                }`}>
                                                {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-[19px] flex justify-end">
                                                <button
                                                onClick={() => handleDownloadInvoice(invoice.invoice_id)}
                                                className="text-blue-600 hover:text-blue-800 flex gap-[7px]"
                                                style={{ color: theme.buttonBackground,display:'flex',justifyContent:'center',alignItems:'center' }}

                                                >
                                                  <span>
                                                  <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                  <path d="M6.99023 7.78662L8.91023 9.49329L10.8302 7.78662" stroke="url(#paint0_linear_1083_10539)" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                                                  <path d="M8.91016 2.6665V9.4465" stroke="url(#paint1_linear_1083_10539)" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                                                  <path d="M15 8.12012C15 11.0668 12.75 13.4535 9 13.4535C5.25 13.4535 3 11.0668 3 8.12012" stroke="url(#paint2_linear_1083_10539)" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                                                  <defs>
                                                  <linearGradient id="paint0_linear_1083_10539" x1="15" y1="13.5" x2="5.18505" y2="-1.13592" gradientUnits="userSpaceOnUse">
                                                  <stop stop-color="#FB5F65"/>
                                                  <stop offset="1" stop-color="#FF3AB6"/>
                                                  </linearGradient>
                                                  <linearGradient id="paint1_linear_1083_10539" x1="15.502" y1="13.9995" x2="2.50195" y2="1.49951" gradientUnits="userSpaceOnUse">
                                                  <stop stop-color="#FB5F65"/>
                                                  <stop offset="1" stop-color="#FF3AB6"/>
                                                  </linearGradient>
                                                  <linearGradient id="paint2_linear_1083_10539" x1="14.5" y1="13" x2="5.05855" y2="-1.05463" gradientUnits="userSpaceOnUse">
                                                  <stop stop-color="#FB5F65"/>
                                                  <stop offset="1" stop-color="#FF3AB6"/>
                                                  </linearGradient>
                                                  </defs>
                                                  </svg>
                                                  </span>
                                                   <span className='gradient-text'>Download</span>
                                                </button>
                                            </td>
                                            </tr>
                                        ))}
                                       
                                    </tbody>
                                </table> : <div style={{alignItems:'center'}} className='text-center flex flex-col p-[35px] justify-center'>
                                            <span>
                                              <img src={theme.noInvoices}/>
                                            </span>
                                            <span className='no-active-plans'>No Invoices Found!</span>
                                        </div>
                                }
                                
                            </div>
                        </div>
                    </div>

                    {/* Cancellation */}
                    {userDetail && userDetail.ogSubscriptionId && (userDetail.ogSubscriptionId.planName!=="noPlan_m" &&  userDetail.ogSubscriptionId.planName!=="free_y") &&
                     <div className="mb-8">
                        <h2 className="billing-h mb-4">Cancellation</h2>
                        <div className="billing-cards p-6 rounded-[20px] shadow-sm border border-gray-100">
                            <div style={{alignItems:'center',width:'100%'}} className="flex justify-between items-start">
                                <div className="flex-1">
                                <div style={{ alignItems:'center'}} className="flex items-start gap-[10px] mb-2">
                                    
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M2 2L12 12M2 12L12 2" stroke="#EB0000" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>

                                    <p className="cancel-h">
                                    Cancel your subscription anytime using the Cancel Plan button.
                                    </p>
                                </div>
                                {/* <p className="cancel-sub-h mb-2">
                                    You'll continue to have access until the end of your billing cycle on 7th February, 2025.
                                </p>
                                <p  style={{ fontStyle: 'italic' }} className="cancel-sub-italic ">
                                    <span className="font-medium">Note:</span> No partial refunds will be issued. For assistance, please
                                    <a href="#" className="hover:underline" >
                                    {" "}
                                    contact support
                                    </a>
                                    .
                                </p> */}
                                </div>
                                <button 
                                    onClick={() => setShowCancelPopup(true)}
                                    className="px-[16px] py-[8px] border border-[#ff363e] text-[#ff363e] cancel-plan rounded-[8px] text-sm hover:bg-red-50 whitespace-nowrap ml-4"
                                    >
                                    Cancel Plan
                                    </button>

                                    {showCancelPopup && (
                                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                                        <div 
                                        className="relative w-[582px] py-[40px] px-[60px] rounded-[10px] shadow-lg"
                                        style={{ 
                                            display:'flex',
                                            flexDirection:'column',
                                            justifyContent:'center',
                                            alignItems:'center',
                                            backgroundColor: theme.background || '#ffffff', // Fallback to white if theme.background is undefined
                                            border: `1px solid ${theme.borderColor}`
                                        }}
                                        >
                                          <div className='cancel-icon-bg px-[12px] py-[13px] rounded-[10px] mb-[19px]'  style={{}}>
                                            <span>  
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z" stroke="url(#paint0_linear_1506_26191)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                            <path d="M7 8.75C8 7.75 9.63 7.75 10.64 8.75" stroke="url(#paint1_linear_1506_26191)" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                                            <path d="M13.3594 8.75C14.3594 7.75 15.9894 7.75 16.9994 8.75" stroke="url(#paint2_linear_1506_26191)" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                                            <path d="M8.4 17.7H15.6C16.1 17.7 16.5 17.3 16.5 16.8C16.5 14.31 14.49 12.3 12 12.3C9.51 12.3 7.5 14.31 7.5 16.8C7.5 17.3 7.9 17.7 8.4 17.7Z" stroke="url(#paint3_linear_1506_26191)" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                                            <defs>
                                            <linearGradient id="paint0_linear_1506_26191" x1="21.4737" y1="17" x2="1.5621" y2="12.749" gradientUnits="userSpaceOnUse">
                                            <stop stop-color="#FB5F65"/>
                                            <stop offset="1" stop-color="#FF3AB6"/>
                                            </linearGradient>
                                            <linearGradient id="paint1_linear_1506_26191" x1="10.5442" y1="8.5625" x2="8.71691" y2="6.66915" gradientUnits="userSpaceOnUse">
                                            <stop stop-color="#FB5F65"/>
                                            <stop offset="1" stop-color="#FF3AB6"/>
                                            </linearGradient>
                                            <linearGradient id="paint2_linear_1506_26191" x1="16.9036" y1="8.5625" x2="15.0763" y2="6.66915" gradientUnits="userSpaceOnUse">
                                            <stop stop-color="#FB5F65"/>
                                            <stop offset="1" stop-color="#FF3AB6"/>
                                            </linearGradient>
                                            <linearGradient id="paint3_linear_1506_26191" x1="16.2632" y1="16.35" x2="7.94739" y2="13.3911" gradientUnits="userSpaceOnUse">
                                            <stop stop-color="#FB5F65"/>
                                            <stop offset="1" stop-color="#FF3AB6"/>
                                            </linearGradient>
                                            </defs>
                                            </svg>

                                            </span>
                                          </div>
                                        {/* Close Button - Top Right */}
                                        <button 
                                        onClick={() => setShowCancelPopup(false)}
                                        className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100"
                                        style={{ color: theme.textColor }}
                                        >
                                        <X className="w-5 h-5" />
                                        </button>

                                        {/* Header */}
                                        <div className="flex justify-center items-center mb-4">
                                            <h3 className="cancelhd" style={{ color: theme.headingColor }}>
                                            Cancel Subscription
                                            </h3>
                                        </div>

                                        {/* Content */}
                                        <div className="mb-[32px]">
                                            <label 
                                            htmlFor="cancelReason"
                                            className="block cancelsub-hd"
                                            style={{ color: theme.textColor, textAlign:'center' }}
                                            >
                                          Is there anything we can do to change your mind? Let us know or continue with cancellation.
                                            </label>
                                            {/* <textarea
                                            id="cancelReason"
                                            rows={4}
                                            className="w-full px-3 py-2 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-pink-500"
                                            style={{
                                                backgroundColor: theme.inputBackground || '#ffffff',
                                                borderColor: theme.borderColor,
                                                color: theme.textColor
                                            }}
                                            placeholder="Your feedback helps us improve our service"
                                            value={cancelReason}
                                            onChange={(e) => setCancelReason(e.target.value)}
                                            /> */}
                                        </div>

                                        {/* Actions */}
                                        <div className="flex justify-end gap-[15px]">
                                            <button
                                            onClick={handleTalkToSupport}
                                            className="px-[18px] py-[13px] talk-support rounded-[12px] text-sm hover:bg-gray-50 transition-colors"
                                            style={{ 
                                               
                                            }}
                                            >
                                           Talk to Support
                                            </button>
                                            {/* <button
                                            onClick={handleCancelPlan}
                                            className="px-[18px] py-[13px] rounded-[12px] border text-sm transition-colors"
                                            style={{
                                                backgroundColor: 'transparent',
                                                color: '#ff363e',
                                                borderColor:'#ff363e'
                                            }}
                                            >
                                           Cancel My Plan
                                            </button> */}
                                        </div>
                                        </div>
                                    </div>
                                    )}
                            </div>
                        </div>
                    </div>}
                </div>
            </div>
        </div>
        <Footer/> 
    </div> 
   
    </>   
  )
}